// Centralized feature flags with safe defaults (all off)
// Toggle order: URL query > localStorage > import.meta.env

const parseBoolean = (v: string | undefined) => {
  if (!v) return false;
  return ['1', 'true', 'on', 'yes'].includes(v.toLowerCase());
};

const getQueryFlag = (key: string): boolean => {
  try {
    const params = new URLSearchParams(window.location.search);
    const val = params.get(key);
    return parseBoolean(val || undefined);
  } catch {
    return false;
  }
};

const getLocalStorageFlag = (key: string): boolean => {
  try {
    const raw = window.localStorage.getItem(key);
    return parseBoolean(raw || undefined);
  } catch {
    return false;
  }
};

const getEnvFlag = (key: string): boolean => {
  // Vite exposes envs on import.meta.env
  const env = (import.meta as unknown as { env?: Record<string, unknown> }).env;
  const raw = env && typeof env[key] === 'string' ? (env[key] as string) : undefined;
  return parseBoolean(raw);
};

export const featureFlags = {
  get FEATURE_CINEMATIC_HERO() {
    // Keys: query=ff_cinematic_hero, localStorage=ff:cinematic_hero, env=VITE_FEATURE_CINEMATIC_HERO
    return (
      getQueryFlag('ff_cinematic_hero') ||
      getLocalStorageFlag('ff:cinematic_hero') ||
      getEnvFlag('VITE_FEATURE_CINEMATIC_HERO')
    );
  },
} as const;

export type FeatureFlags = typeof featureFlags;
// Feature flags for stabilizing MVP scope
export const FEATURES = {
  experimentalPartialRestore: false,
  experimentalClone: false,
  showDiffSummary: true,
  allowFullRestore: true,
  allowRevisionList: true
} as const;

export type FeatureKey = keyof typeof FEATURES;
export function isFeatureEnabled(key: FeatureKey) { return FEATURES[key]; }